import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook that provides a navigate function that preserves query parameters
 * Useful for maintaining tracking params like ?source=marketplace across navigations
 */
export function usePreserveQueryParams() {
  const navigate = useNavigate();
  const location = useLocation();

  const navigateWithParams = useCallback(
    (to: string, options?: { replace?: boolean; state?: unknown }) => {
      // Check if the destination already has query params
      const hasQueryParams = to.includes('?');
      
      // Get current query params
      const currentParams = location.search;
      
      if (currentParams && !hasQueryParams) {
        // Append current query params to destination
        navigate(`${to}${currentParams}`, options);
      } else if (currentParams && hasQueryParams) {
        // Merge query params (current params take precedence for new ones)
        const destUrl = new URL(to, window.location.origin);
        const currentSearchParams = new URLSearchParams(currentParams);
        
        // Add current params that don't exist in destination
        currentSearchParams.forEach((value, key) => {
          if (!destUrl.searchParams.has(key)) {
            destUrl.searchParams.set(key, value);
          }
        });
        
        navigate(`${destUrl.pathname}${destUrl.search}`, options);
      } else {
        // No current params, navigate normally
        navigate(to, options);
      }
    },
    [navigate, location.search]
  );

  return { navigateWithParams, currentSearch: location.search };
}
